import { useApolloClient } from "@apollo/client";
import { CardContainer, ModalOpenButton } from "@msys/ui";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useParams } from "react-router-dom";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import { LogoutButton } from "../../auth/LogoutButton.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { SwicthOrganisationButton } from "../../auth/SwicthOrganisationButton.js";
import { useAuth } from "../../auth/useAuth.js";
import { useAvailableUsers } from "../../auth/useAvailableUsers.js";
import { useUserData } from "../../auth/useUserData.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageGrid } from "../../commons/layout/PageGrid.js";
import { ConfirmModal } from "../../commons/modals/ConfirmModal.js";
import { PageNotFound } from "../../commons/PageNotFound.js";
import { CancelOrganisationMembershipInvitationButton } from "../../features/invitations/CancelOrganisationMembershipInvitationButton.js";
import { InviteOrganisationMemberModal } from "../../features/invitations/InviteOrganisationMemberModal.js";
import { ResendOrganisationMembershipInvitationEmailModal } from "../../features/invitations/ResendOrganisationMembershipInvitationEmailModal.js";
import {
  MyOrganisationUserAvatarAndTitleData,
  ViewerAvatarAndTitleForm,
} from "../../features/organisations/MyOrganisationUserAvatarAndTitle.js";
import { DeactivateUserButton } from "../../features/users/DeactivateUserButton.js";
import { OrganisationMembershipStatusLabel } from "../../features/users/OrganisationMembershipStatusLabel.js";
import { ReactivateUserButton } from "../../features/users/ReactivateUserButton.js";
import { UserInformationBox } from "../../features/users/UserInformationBox.js";
import { UserInformationForm } from "../../features/users/UserInformationForm.js";
import { UserRolesBox } from "../../features/users/UserRolesBox.js";
import { UserRolesForm } from "../../features/users/UserRolesForm.js";
import { assertNever } from "../../utils.js";
import {
  useMyOrganisationUserProfileQuery,
  useSetMemberAsOrganisationRepresentativeMutation,
} from "./OrganisationUserProfile.generated.js";
import { ResetPasswordButton } from "../../auth/ResetPasswordButton.js";
import { useSapProfileActionSettings } from "../../features/sap-s4-hana/hooks.js";

interface Props {
  submenuItems: PageTopbarItem[];
  userId?: string;
}

export function MyOrganisationUserProfile({
  submenuItems,
  userId: userIdProp,
}: Props) {
  const { userId: userIdParam } = useParams();
  const userId = userIdProp ?? userIdParam;
  if (!userId) throw new Error("User id is missing");

  const auth = useAuth();
  const viewer = useUserData().currentUser!;
  const { availableUsers } = useAvailableUsers();
  const { hideResetPassword } = useSapProfileActionSettings();

  const { t } = useTranslate(["UserProfile", "OrganisationInvite"]);

  const client = useApolloClient();
  const query = useMyOrganisationUserProfileQuery({
    client,
    variables: { userId },
    fetchPolicy: "network-only",
  });
  const user = query.data?.user;
  const organisationRepresentativeId =
    query.data?.viewer?.organisation.representive?.id;

  const [setMemberAsOrganisationRepresentative] =
    useSetMemberAsOrganisationRepresentativeMutation({ client });

  if (!query.loading && user?.organisationId !== viewer.organisation.id)
    return <PageNotFound />;

  const isViewersUser = viewer.id === userId;

  return (
    <Page
      title={user ? `${user.firstname} ${user.familyname}` : ""}
      submenuItems={submenuItems}
    >
      {user && (
        <PageContainer>
          <PageGrid columns={{ xs: 1, md: 2, xl: 4 }}>
            <>
              <CardContainer>
                <Stack direction="column" p={1} spacing={1}>
                  {isViewersUser ? (
                    <ViewerAvatarAndTitleForm user={user} />
                  ) : (
                    <MyOrganisationUserAvatarAndTitleData user={user} />
                  )}

                  {isViewersUser ? (
                    <UserInformationForm key={`${user.id}-form`} user={user} />
                  ) : (
                    <RestrictedByOrganisationPermissionWithDebug
                      permission="MANAGE_ORG"
                      otherwise={
                        <UserInformationBox
                          key={`${user.id}-info-box`}
                          user={user}
                        />
                      }
                    >
                      <UserInformationForm
                        key={`${user.id}-info-form`}
                        user={user}
                        canEditEmail={user.membershipStatus === "UNACTIVATED"}
                      />
                    </RestrictedByOrganisationPermissionWithDebug>
                  )}
                </Stack>
              </CardContainer>

              {isViewersUser && (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    {!hideResetPassword && (
                      <ResetPasswordButton variant="outlined" color="primary" />
                    )}
                    <LogoutButton type="button" />
                  </Stack>
                  {(availableUsers.length > 1 ||
                    auth.belongsToGroup("org-creator")) && (
                    <Box>
                      <SwicthOrganisationButton fullWidth />
                    </Box>
                  )}
                </>
              )}
            </>
            <>
              <CardContainer
                title={
                  <Stack
                    direction={"row"}
                    spacing={1}
                    alignItems="center"
                    justifyContent={"space-between"}
                  >
                    <span>{t("Membership", { ns: "UserProfile" })}</span>
                    {user.id === organisationRepresentativeId && (
                      <Tooltip
                        key="representive-icon"
                        title={t("Organisation representative", {
                          ns: "UserProfile",
                        })}
                      >
                        <AccountCircleIcon />
                      </Tooltip>
                    )}
                    <OrganisationMembershipStatusLabel
                      membershipStatus={user.membershipStatus}
                      invitation={user.membershipInvitation ?? null}
                      wrap
                    />
                  </Stack>
                }
              >
                <Stack direction="column" p={1} spacing={1}>
                  {user.membershipStatus === "UNACTIVATED" ? (
                    user.membershipInvitation ? (
                      <>
                        {user.membershipInvitation.status === "PENDING" ? (
                          <Alert severity="info">
                            {t(
                              "This member has been invited but is not yet activated on MeisterSystems",
                              {
                                ns: "UserProfile",
                              }
                            )}
                          </Alert>
                        ) : user.membershipInvitation.status === "DECLINED" ? (
                          <Alert severity="warning">
                            {t(
                              "This member has declined membership invitation",
                              {
                                ns: "OrganisationInvite",
                              }
                            )}
                          </Alert>
                        ) : null}
                        <Box>
                          <Grid container columns={2} spacing={1}>
                            <Grid size={1}>
                              <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_ORG">
                                <ModalOpenButton
                                  Modal={
                                    ResendOrganisationMembershipInvitationEmailModal
                                  }
                                  modalProps={{
                                    invitationId: user.membershipInvitation.id,
                                    refetchQueries: [
                                      namedOperations.Query
                                        .MyOrganisationUserProfile,
                                    ],
                                  }}
                                >
                                  <Button variant="contained" fullWidth>
                                    {t("Resend invitation", {
                                      ns: "UserProfile",
                                    })}
                                  </Button>
                                </ModalOpenButton>
                              </RestrictedByOrganisationPermissionWithDebug>
                            </Grid>
                            <Grid size={1}>
                              <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_ORG">
                                <CancelOrganisationMembershipInvitationButton
                                  invitationId={user.membershipInvitation.id}
                                  refetchQueries={[
                                    namedOperations.Query
                                      .MyOrganisationUserProfile,
                                  ]}
                                  fullWidth
                                />
                              </RestrictedByOrganisationPermissionWithDebug>
                            </Grid>
                          </Grid>
                        </Box>
                        <Divider />
                        <RestrictedByOrganisationPermissionWithDebug
                          permission="MANAGE_ORG"
                          otherwise={
                            <UserRolesBox
                              key={`${user.id}-roles-box`}
                              user={user}
                            />
                          }
                        >
                          <UserRolesForm
                            key={`${user.id}-roles-form`}
                            user={user}
                          />
                        </RestrictedByOrganisationPermissionWithDebug>
                      </>
                    ) : (
                      <>
                        <Alert severity="info">
                          {t(
                            "This member is not yet activated on MeisterSystems",
                            { ns: "UserProfile" }
                          )}
                        </Alert>
                        <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_ORG">
                          <ModalOpenButton
                            Modal={InviteOrganisationMemberModal}
                            modalProps={{
                              userId: user.id,
                              refetchQueries: [
                                namedOperations.Query.MyOrganisationUserProfile,
                              ],
                            }}
                          >
                            <Button variant="contained">
                              {t("Invite to use MeisterSystems", {
                                ns: "UserProfile",
                              })}
                            </Button>
                          </ModalOpenButton>
                        </RestrictedByOrganisationPermissionWithDebug>

                        <Divider />
                        <RestrictedByOrganisationPermissionWithDebug
                          permission="MANAGE_ORG"
                          otherwise={
                            <UserRolesBox
                              key={`${user.id}-roles-box`}
                              user={user}
                            />
                          }
                        >
                          <UserRolesForm
                            key={`${user.id}-roles-form`}
                            user={user}
                          />
                        </RestrictedByOrganisationPermissionWithDebug>
                      </>
                    )
                  ) : user.membershipStatus === "ACTIVATED" ? (
                    <>
                      <TextField
                        type="email"
                        label={t("Email", { ns: "UserProfile" })}
                        disabled={true}
                        value={user.email}
                      />
                      <RestrictedByOrganisationPermissionWithDebug
                        permission="MANAGE_ORG"
                        otherwise={
                          <UserRolesBox
                            key={`${user.id}-roles-box`}
                            user={user}
                          />
                        }
                      >
                        <UserRolesForm
                          key={`${user.id}-roles-form`}
                          user={user}
                        />
                      </RestrictedByOrganisationPermissionWithDebug>
                      <Divider />
                      <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_ORG">
                        <ModalOpenButton
                          Modal={ConfirmModal}
                          modalProps={{
                            title: t("Set as organisation representative", {
                              ns: "UserProfile",
                            }),
                            text: t(
                              "Are you sure you want to set this user as organisation representative?",
                              { ns: "UserProfile" }
                            ),
                            handleConfirm: () => {
                              setMemberAsOrganisationRepresentative({
                                variables: {
                                  organisationId: viewer.organisation.id,
                                  userId,
                                },
                              });
                            },
                          }}
                          disabled={user.id === organisationRepresentativeId}
                        >
                          <Button variant="contained" onClick={() => {}}>
                            {t("Set as organisation representative", {
                              ns: "UserProfile",
                            })}
                          </Button>
                        </ModalOpenButton>
                      </RestrictedByOrganisationPermissionWithDebug>
                      <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_ORG">
                        <DeactivateUserButton
                          userId={user.id}
                          refetchQueries={[
                            namedOperations.Query.MyOrganisationUserProfile,
                          ]}
                          disabled={
                            user.id === viewer.id ||
                            user.id === organisationRepresentativeId
                          }
                        >
                          <Button variant="outlined" onClick={() => {}}>
                            {t("Deactivate member", { ns: "UserProfile" })}
                          </Button>
                        </DeactivateUserButton>
                      </RestrictedByOrganisationPermissionWithDebug>
                    </>
                  ) : user.membershipStatus === "DEACTIVATED" ? (
                    <>
                      <Alert severity="warning">
                        {t("This member has been deactivated", {
                          ns: "UserProfile",
                        })}
                      </Alert>
                      <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_ORG">
                        <ReactivateUserButton userId={user.id}>
                          <Button variant="contained">
                            {t("Reactivate member", { ns: "UserProfile" })}
                          </Button>
                        </ReactivateUserButton>
                      </RestrictedByOrganisationPermissionWithDebug>
                      <Divider />
                      <RestrictedByOrganisationPermissionWithDebug
                        permission="MANAGE_ORG"
                        otherwise={
                          <UserRolesBox
                            key={`${user.id}-roles-box`}
                            user={user}
                          />
                        }
                      >
                        <UserRolesForm
                          key={`${user.id}-roles-form`}
                          user={user}
                        />
                      </RestrictedByOrganisationPermissionWithDebug>
                    </>
                  ) : (
                    assertNever(user.membershipStatus)
                  )}
                </Stack>
              </CardContainer>
            </>
          </PageGrid>
        </PageContainer>
      )}
    </Page>
  );
}
